import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';

const DARK = {
  page: '#0f172a', surface: '#1e293b', surfaceDeep: '#162032',
  border: '#334155', text: '#f1f5f9', textSecondary: '#94a3b8',
  textMuted: '#64748b', successText: '#4ade80', dangerText: '#fca5a5',
  warnText: '#fbbf24', blueText: '#60a5fa', purpleText: '#c084fc',
};
const LIGHT = {
  page: '#f8fafc', surface: '#ffffff', surfaceDeep: '#f1f5f9',
  border: '#e2e8f0', text: '#0f172a', textSecondary: '#475569',
  textMuted: '#94a3b8', successText: '#16a34a', dangerText: '#dc2626',
  warnText: '#d97706', blueText: '#2563eb', purpleText: '#7c3aed',
};

const FeedbackDashboard = ({ dark = true }) => {
  const t = dark ? DARK : LIGHT;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/feedback-cache/dashboard`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) {
      console.error('Dashboard load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: t.textMuted }}>Loading dashboard...</div>;
  }

  const d = data || {};
  const llm = d.llm_generated || 0;
  const cached = d.from_feedback_cache || 0;
  const total = d.total_queries || 0;
  const cacheRate = total > 0 ? ((cached / total) * 100).toFixed(1) : '0.0';
  const llmRate = total > 0 ? ((llm / total) * 100).toFixed(1) : '0.0';
  const domains = d.domain_stats || [];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: t.text }}>Query Generation Dashboard</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: t.textMuted }}>Last 30 days overview</p>
        </div>
        <button onClick={loadData} style={{ padding: '8px 16px',
          background: dark ? '#1d4ed820' : '#eff6ff',
          border: `1px solid ${dark ? '#1d4ed840' : '#bfdbfe'}`,
          borderRadius: 8, cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 500 }}>
          Refresh
        </button>
      </div>

      {/* Main stat cards — LLM vs Feedback */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
        <StatCard t={t} label="Total Queries" value={total} icon="📋" color={t.blueText}
          subtitle="All queries in last 30 days" />
        <StatCard t={t} label="LLM Generated" value={llm} icon="🤖" color={t.purpleText}
          subtitle={`${llmRate}% of total`} />
        <StatCard t={t} label="From Feedback Cache" value={cached} icon="🗄️" color={t.successText}
          subtitle={`${cacheRate}% of total — saved LLM calls`} />
      </div>

      {/* Secondary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard t={t} label="Successful" value={d.successful || 0} icon="✅" color={t.successText} />
        <StatCard t={t} label="Failed" value={d.failed || 0} icon="❌" color={t.dangerText} />
        <StatCard t={t} label="Feedback Entries" value={d.total_feedback_entries || 0} icon="🔧" color={t.warnText} />
        <StatCard t={t} label="Inactive Entries" value={d.inactive_entries || 0} icon="🚫" color={t.dangerText} />
        <StatCard t={t} label="Domains Active" value={`${d.domains_with_entries || 0}/13`} icon="📂" color={t.blueText} />
      </div>

      {/* Visual bar — LLM vs Cache ratio */}
      {total > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: t.text }}>Query Source Distribution</h3>
          <div style={{ display: 'flex', height: 40, borderRadius: 10, overflow: 'hidden', border: `1px solid ${t.border}` }}>
            <div style={{ width: `${llmRate}%`, background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700, minWidth: llm > 0 ? 60 : 0 }}>
              {llm > 0 && `LLM ${llmRate}%`}
            </div>
            <div style={{ width: `${cacheRate}%`, background: 'linear-gradient(90deg, #16a34a, #4ade80)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700, minWidth: cached > 0 ? 60 : 0 }}>
              {cached > 0 && `Cache ${cacheRate}%`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 12, color: t.textMuted }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#7c3aed', marginRight: 6 }}/>LLM Generated ({llm})</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#16a34a', marginRight: 6 }}/>From Feedback Cache ({cached})</span>
          </div>
        </div>
      )}

      {/* Domain breakdown */}
      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: t.text }}>Feedback Entries by Domain</h3>
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 140px', gap: 16, padding: '12px 20px',
          background: t.surfaceDeep, borderBottom: `1px solid ${t.border}`, fontSize: 12, fontWeight: 700,
          color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>Domain</span><span>Active</span><span>Inactive</span><span>Total</span><span>Last Updated</span>
        </div>
        {domains.map(dm => (
          <div key={dm.domain} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 140px',
            gap: 16, padding: '12px 20px', borderBottom: `1px solid ${t.border}`, fontSize: 14 }}>
            <span style={{ color: t.text, fontWeight: 600, textTransform: 'capitalize' }}>{dm.domain.replace('_', ' ')}</span>
            <span style={{ color: t.successText, fontWeight: 600 }}>{dm.active || 0}</span>
            <span style={{ color: dm.inactive > 0 ? t.dangerText : t.textMuted, fontWeight: 600 }}>{dm.inactive || 0}</span>
            <span style={{ color: t.textSecondary }}>{dm.total || 0}</span>
            <span style={{ color: t.textMuted, fontSize: 12 }}>{dm.last_updated ? new Date(dm.last_updated).toLocaleDateString() : '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ t, label, value, icon, color, subtitle }) => (
  <div style={{ padding: '20px', background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 13, color: t.textMuted, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 18 }}>{icon}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
    {subtitle && <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>{subtitle}</div>}
  </div>
);

export default FeedbackDashboard;
